import React, { useState, useRef } from "react";
import ReactPlayer from "react-player";
import { Box, IconButton, CircularProgress } from "@mui/material";
import { PlayArrow, Pause, Fullscreen } from "@mui/icons-material";

const Video = ({ path }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [hasStarted, setHasStarted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const playerRef = useRef(null);

  const togglePlayPause = () => {
    if (!hasStarted) {
      setHasStarted(true);
      setIsLoading(true);
      setIsPlaying(true);
    } else if (isLoading) {
      // If currently loading, cancel the loading and reset
      setIsLoading(false);
      setIsPlaying(false);
    } else {
      setIsPlaying((prev) => !prev);
    }
  };

  const handleFullscreen = () => {
    const playerElement = playerRef.current?.getInternalPlayer();
    if (playerElement && playerElement.requestFullscreen) {
      playerElement.requestFullscreen();
    }
  };

  const handleMouseEnter = () => setShowControls(true);
  const handleMouseLeave = () => setShowControls(false);

  return (
    <Box
      mt={4}
      position="relative"
      data-aos="fade-up"
      data-aos-delay={300}
      overflow="hidden"
      width="100%"
      bgcolor="#ffffff10"
      sx={{
        aspectRatio: "16/9", // Give the video a proper aspect ratio
        borderRadius: "32px",
        "& video": {
          borderRadius: "32px !important",
          overflow: "hidden",
        },
        "& .react-player": {
          width: "100% !important",
          height: "100% !important",
        },
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Poster image - shown before video starts */}
      {!hasStarted && (
        <Box
          component="img"
          src={path + ".jpg"}
          alt="Video thumbnail"
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: "32px",
            zIndex: 1,
          }}
        />
      )}
      
      <ReactPlayer
        ref={playerRef}
        url={path + ".webm"}
        playing={isPlaying}
        controls={false}
        width="100%"
        height="100%"
        playsinline
        onReady={() => {
          setIsLoading(false);
        }}
        onStart={() => {
          setIsLoading(false);
        }}
        onPlay={() => {
          setIsLoading(false);
        }}
        onPause={() => {
          setIsPlaying(false);
        }}
        onEnded={() => {
          setIsPlaying(false);
        }}
        style={{
          opacity: hasStarted ? 1 : 0,
          transition: "opacity 0.3s ease",
        }}
        config={{
          file: {
            attributes: {
              poster: path + ".jpg",
            },
          },
        }}
      />

      {/* Custom Controls */}
      {showControls && (
        <>
          {/* Play/Pause Button - Center */}
          <IconButton
            onClick={togglePlayPause}
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "rgba(0, 0, 0, 0.6)",
              color: "white",
              borderRadius: "50%",
              width: 64,
              height: 64,
              transition: "all 0.3s ease",
              zIndex: 2,
              "&:hover": {
                bgcolor: "rgba(0, 0, 0, 0.8)",
                transform: "translate(-50%, -50%) scale(1.1)",
              },
            }}
          >
            {isLoading ? (
              <CircularProgress size={24} sx={{ color: "white.main" }} />
            ) : isPlaying ? (
              <Pause fontSize="large" />
            ) : (
              <PlayArrow fontSize="large" />
            )}
          </IconButton>

          {/* Fullscreen Button - Bottom Right */}
          {hasStarted && (
            <IconButton
              onClick={handleFullscreen}
              sx={{
                position: "absolute",
                bottom: 16,
                right: 16,
                bgcolor: "rgba(0, 0, 0, 0.6)",
                color: "white",
                borderRadius: "8px",
                width: 40,
                height: 40,
                transition: "all 0.3s ease",
                zIndex: 2,
                "&:hover": {
                  bgcolor: "rgba(0, 0, 0, 0.8)",
                },
              }}
            >
              <Fullscreen fontSize="small" />
            </IconButton>
          )}
        </>
      )}
    </Box>
  );
};

export default Video;
